import Peek from '../Peek.jsx';
import { EstateEditor, CoverageEditor } from '../Editable.jsx';
import Explainer from '../Explainer.jsx';

// What protects your money.
//
// Everything earlier is what the household has; this is what stands between
// it and the things that go wrong. Three states, and the states do the
// talking: "in place" is a fact from the questionnaire, "not in place" is the
// advisor having asked, and "not yet discussed" is an agenda item. None is
// ever dressed up as another, and the reason a missing item matters prints
// only when it is missing.

// How the pieces fit together, for a client reading the page afterwards. True
// of any household, so none of it is written per client.
const HOW = [
  {
    title: 'A trust and a will do different jobs',
    text: 'A living trust passes on what it holds without probate, and it only holds what has been retitled into it. '
      + 'A will covers whatever was left outside the trust, and names guardians for children.',
  },
  {
    title: 'Beneficiary forms come first',
    text: 'Retirement accounts and life insurance go to whoever is named on the form, whatever the will or the trust says, '
      + 'so the forms are worth checking whenever the family changes.',
  },
  {
    title: 'Powers of attorney are for while you are living',
    text: 'They let someone you chose handle your money and your medical decisions if you cannot. '
      + 'Without them, a court appoints someone.',
  },
  {
    title: 'Insurance is for what savings cannot absorb',
    text: 'A long illness, a lawsuit or the loss of an income can undo decades of saving. '
      + 'Planning weighs which of those to insure and which to carry yourself.',
  },
];

const STATUS = {
  'in-place': { label: 'In place', cls: 'is-inplace' },
  none: { label: 'Not in place', cls: 'is-none' },
  unknown: { label: 'Not yet discussed', cls: 'is-unknown' },
  // Nothing on the questionnaire records it, so it is never "in place" here.
  verify: { label: 'Verify and update', cls: 'is-verify' },
};

function Row({ item, peek }) {
  const s = STATUS[item.status] || STATUS.unknown;
  const badge = <span className={`conv-prot-badge ${s.cls}`}>{s.label}</span>;
  return (
    <div className="conv-prot-row">
      {peek && !item.fixed ? <Peek title={item.label} editor={peek}>{badge}</Peek> : badge}
      <div>
        <div className="conv-prot-label">{item.label}</div>
        {item.status !== 'in-place' ? <p>{item.why}</p> : null}
      </div>
    </div>
  );
}

export default function Protection({ checklist }) {
  if (!checklist) return null;
  const { docs, coverage, yearsEstablished, counts, anythingKnown } = checklist;
  return (
    <>
      <div className="conv-module-head">
        <h2>What protects your money</h2>
        {anythingKnown ? (
          <span className="conv-quiet">{`${counts.inPlace} in place · ${counts.none} missing · ${counts.unknown} to discuss`}</span>
        ) : null}
      </div>
      <div className="conv-prot-cols">
        <section>
          <h3>Estate documents</h3>
          {yearsEstablished ? <p className="conv-quiet">{`Last established or updated: ${yearsEstablished}.`}</p> : null}
          {docs.map(d => <Row item={d} key={d.key} peek={() => <EstateEditor />} />)}
        </section>
        <section>
          <h3>Coverage</h3>
          {coverage.map(c => <Row item={c} key={c.key} peek={() => <CoverageEditor kind={c.key} />} />)}
        </section>
      </div>
      {docs.some(d => d.status === 'none' || d.status === 'unknown') ? (
        <div className="conv-callout">
          <strong>Without these documents, a judge, not you, decides who manages your affairs.</strong>{' '}
          Probate is public, slow and costly, and it is avoidable.
        </div>
      ) : null}
      <Explainer title="How these fit together" items={HOW} />
    </>
  );
}
